import { Alert, Button, cn, Link, Skeleton } from '@heroui/react'
import { useAuth } from 'hooks/useAuth'
import { useVoices } from 'hooks/useVoices'
import { FileAudio, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { RiSpeakAiLine } from 'react-icons/ri'
import AddVoiceSampleModal from './AddVoiceSampleModal'
import CreateVoiceCloneModal from './CreateVoiceCloneModal'
import DeleteSampleModal from './DeleteSampleModal'
import DisplayTaggedText from './DisplayTaggedText'
import PixelAvatar from './PixelAvatar'
import SampleEditor from './SampleEditor'
import TestScriptEditor from './TestScriptEditor'
import { supportedEffects, useSamples, useStudioStore } from './utils'

export default function VoiceStudio() {
  const { user, org } = useAuth()
  const selectedCloneId = useStudioStore(state => state.selectedCloneId)
  const setSelectedCloneId = useStudioStore(state => state.setSelectedCloneId)
  const [selectedSampleId, setSelectedSampleId] = useState(null)

  const { data: voices, isFetching: isFetchingVoices } = useVoices()
  const selectedClone = useMemo(() => voices?.find(v => v.id === selectedCloneId), [selectedCloneId, voices])

  const beigeVoices = useMemo(
    () => voices?.filter(
      voice =>
        voice.user_id !== null
        && (voice.user_id === user?.user?.id || voice.user_id === org?.id)
        && voice.tags?.some(tag => tag?.toLowerCase().includes('beige')),
    ),
    [voices, isFetchingVoices],
  )

  const { data: samples, isLoading: isLoadingSamples } = useSamples(selectedCloneId)

  const handleCloneSelect = (clone) => {
    setSelectedCloneId(clone.id)
    setSelectedSampleId(null)
  }

  // Render sidebar with clones and samples
  const renderSidebar = () => (
    <div style={{ scrollbarWidth: 'thin' }} className="w-1/4 border-r border-gray-200 p-4 pt-0 min-w-[300px] h-full max-h-full flex flex-col gap-4 overflow-y-auto bg-gray-50">

      <span className="flex items-center border-b border-gray-300 justify-between pt-4">
        <p className="text-sm font-semibold tracking-tight text-black">Cloned Voices</p>
        <CreateVoiceCloneModal>
          {({ onPress }) => (
            <Button size="sm" isIconOnly color="primary" variant="light" fullWidth onPress={onPress}>
              <Plus size={14} />
            </Button>
          )}
        </CreateVoiceCloneModal>
      </span>

      {beigeVoices?.length === 0
        ? (
            <h1 className="text-lg tracking-tight text-gray-500 mt-24 mb-6 text-center">
              No Beige Voices yet.
            </h1>
          )
        : (
            <div className="space-y-4">
              {beigeVoices?.map(clone => (
                <div key={clone.id} className="space-y-2">
                  <div
                    className={cn(
                      'p-3 bg-white border hover:bg-gray-50 transition-colors duration-200 cursor-pointer rounded-sm',
                      selectedCloneId === clone.id && !selectedSampleId ? 'bg-primary-50 border-primary-500' : 'border-gray-200',
                    )}
                    onClick={() => handleCloneSelect(clone)}
                  >
                    <div className="flex items-center space-x-2">
                      <PixelAvatar name={clone.id} isAnimating={selectedCloneId === clone.id && !selectedSampleId} />
                      <div>
                        <p className="font-medium text-sm">{clone.name}</p>
                        <p className="text-xs text-gray-500">{clone.description || 'No description'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Show samples if this clone is selected */}
                  {selectedCloneId === clone.id && (
                    <div className="ml-4 space-y-1 mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-2">Samples</p>

                      {isLoadingSamples
                        ? (Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-6 w-full" />
                          )))
                        : !samples?.length
                            ? (
                                <p className="text-xs text-gray-400 italic">No samples yet</p>
                              )
                            : (
                                <>
                                  {samples.map(sample => (
                                    <div
                                      key={sample.id}
                                      className={cn(
                                        'h-8 px-2 text-xs hover:bg-gray-100 cursor-pointer transition-colors duration-100 rounded-sm flex items-center justify-between',
                                        selectedSampleId === sample.id ? 'bg-gray-100 font-medium' : '',
                                      )}
                                      onClick={() => setSelectedSampleId(sample.id)}
                                    >
                                      <div className="flex items-center flex-1 min-w-0">
                                        <FileAudio size={12} className="mr-1 text-gray-500 shrink-0" />
                                        <span className="truncate">
                                          {sample.transcription
                                            ? (
                                                <DisplayTaggedText
                                                  text={sample.transcription}
                                                  supportedEffects={supportedEffects}
                                                  compact
                                                />
                                              )
                                            : 'Untitled Sample'}
                                        </span>
                                      </div>
                                      {selectedSampleId === sample.id && samples?.length > 1 && (
                                        <DeleteSampleModal voiceId={selectedCloneId} sampleId={sample.id} setSelectedSampleId={setSelectedSampleId} />
                                      )}
                                    </div>
                                  ))}
                                  {samples?.length < 5 && (
                                    <div className="pt-1">
                                      <AddVoiceSampleModal voiceId={selectedCloneId} />
                                    </div>
                                  )}
                                </>
                              )}
                    </div>
                  )}
                </div>

              ))}
            </div>
          )}

      <Alert
        color="primary"
        hideIcon
        className="max-w-md mt-auto bg-white items-center font-taurus !p-2 !flex-none"
        radius="sm"
        endContent={(
          <Button as={Link} href="/enterprise" color="warning" size="sm" variant="flat">
            Talk to Sales
          </Button>
        )}
        title="Build with Bland TTS"
        variant="faded"
      />
    </div>
  )

  return (
    <div className="flex h-[calc(100vh-220px)] border border-gray-200 rounded-sm">
      {renderSidebar()}
      <div className="flex-1 overflow-y-auto">
        {selectedSampleId
          ? <SampleEditor selectedSampleId={selectedSampleId} selectedCloneId={selectedCloneId} />
          : selectedClone
            ? <TestScriptEditor selectedClone={selectedClone} setSelectedCloneId={setSelectedCloneId} />
            : (
                <div className="flex flex-col h-full items-center justify-center p-12  rounded-md border border-gray-200">
                  <RiSpeakAiLine size={54} className="text-gray-400 mb-4" />
                  <h3 className="text-2xl tracking-tight font-semibold text-center">Select a Voice Clone</h3>
                  <p className="text-gray-500 text-sm max-w-lg mt-1 text-center mb-6">
                    Choose a voice clone from the sidebar to start testing and experimenting with our LLM-based text-to-speech technology.
                  </p>
                  <CreateVoiceCloneModal>
                    {({ onPress }) => (
                      <Button color="primary" variant="flat" radius="sm" onPress={onPress}>
                        <Plus size={16} />
                        Create New Voice Clone
                      </Button>
                    )}
                  </CreateVoiceCloneModal>
                </div>
              )}
      </div>
    </div>
  )
}
